import React, { useState, useEffect } from "react";
import { CRUD_API_EDITPROFILE } from "../../hooks/APIHooks";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/GlobalProvider";
import LogoLoader from "../../components/LogoLoader/LogoLoader";
import { 
  FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, 
  FaUniversity, FaCreditCard, FaCode, FaQrcode, 
  FaVenusMars, FaLock, FaCheckCircle, FaExclamationTriangle,
  FaInfoCircle
} from "react-icons/fa";
import { Dashboard_Profile } from "../../assets/data/Imagedata";

const ProfileEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userData, setUserData] = useState({
    user_id: "",
    emailaddress: "",
    phonenumber: "",
    sex: "",
    address: "",
    bank_account_number: "",
    ifsc_code: "",
    bank_name: "",
    upi_id: "",
    allow_edit: 1,
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userId = user.user_id;
        const response = await fetch(
          `${CRUD_API_EDITPROFILE}?user_id=${userId}`
        );
        const data = await response.json();

        if (response.ok) {
          setUserData(data);
          const nameParts = data.name ? data.name.split(" ") : ["", ""];
          setFirstName(nameParts[0] || "");
          setLastName(nameParts.slice(1).join(" ") || "");
        } else {
          setError(data.message || "Failed to fetch user data.");
        }
      } catch (err) {
        console.error("Fetch error", err);
        setError("An error occurred while fetching user data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const onUpdatePress = async () => {
    setError("");
    setSuccessMessage("");

    if (userData.allow_edit === 0) {
      setError("You are not allowed to edit bank account details.");
      return;
    }

    try {
      const response = await fetch(CRUD_API_EDITPROFILE, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userData.user_id,
          name: `${firstName} ${lastName}`,
          emailaddress: userData.emailaddress,
          phonenumber: userData.phonenumber,
          sex: userData.sex,
          address: userData.address,
          bank_account_number: userData.bank_account_number,
          ifsc_code: userData.ifsc_code,
          bank_name: userData.bank_name,
          upi_id: userData.upi_id,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        setSuccessMessage("Profile updated successfully");
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        setError(data.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error("Update error", err);
      setError("An error occurred. Please try again.");
    }
  };

  if (isLoading) return <LogoLoader />;

  return (
    <div className="flex flex-col items-center justify-start bg-primary-100 min-h-screen p-4 pb-20">
      <div className="w-full max-w-xl space-y-6">
        
        {/* Profile Header Card */}
        <div className="bg-primary rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center border border-white/5">
          <div className="absolute top-0 left-0 w-full h-2 bg-secondary"></div>
          <div className="relative inline-block mb-4">
            <img 
              src={Dashboard_Profile} 
              alt="Profile" 
              className="w-24 h-24 rounded-full border-4 border-secondary/30 p-1 mx-auto"
            />
            <div className="absolute bottom-0 right-0 bg-secondary text-primary p-2 rounded-full border-4 border-primary">
              <FaUser size={14} />
            </div>
          </div>
          <h1 className="text-2xl font-black text-white italic uppercase tracking-tighter">Edit Profile</h1>
          <p className="text-gray-400 text-[10px] uppercase tracking-widest font-bold mt-1">Manage your account and bank details</p>
        </div>

        {/* Warning Message */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 flex items-start gap-3">
          <FaInfoCircle className="text-blue-400 mt-1 flex-shrink-0" />
          <p className="text-blue-400 text-xs font-medium">
            Please logout and sign back in after updating your profile to see changes reflected across the app.
          </p>
        </div>

        <div className="bg-primary rounded-3xl p-6 shadow-2xl border border-white/5 space-y-8">
          
          {/* Section: Personal Information */}
          <div className="space-y-4">
            <h2 className="text-secondary text-[11px] font-black uppercase tracking-widest flex items-center gap-2">
              <span className="w-1 h-3 bg-secondary rounded-full"></span>
              Personal Information
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* First Name */}
                <div className="relative group">
                  <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                  <input
                    type="text"
                    value={firstName}
                    disabled={userData.allow_edit === 0}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                  />
                </div>
                {/* Last Name */}
                <div className="relative group">
                  <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                  <input
                    type="text"
                    value={lastName}
                    disabled={userData.allow_edit === 0}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="relative group">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <input
                  type="email"
                  name="emailaddress"
                  value={userData.emailaddress}
                  disabled={userData.allow_edit === 0}
                  placeholder="Email Address"
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                />
              </div>

              {/* Phone (Read Only) */}
              <div className="relative">
                <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
                <input
                  type="tel"
                  name="phonenumber"
                  value={userData.phonenumber}
                  readOnly
                  className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-gray-500 cursor-not-allowed italic text-sm"
                />
              </div>

              {/* Gender Select */}
              <div className="relative group">
                <FaVenusMars className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <select
                  name="sex"
                  value={userData.sex}
                  disabled={userData.allow_edit === 0}
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white appearance-none focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                >
                  <option value="" className="bg-primary text-white">Select Gender</option>
                  <option value="Male" className="bg-primary text-white">Male</option>
                  <option value="Female" className="bg-primary text-white">Female</option>
                </select>
              </div>

              {/* Address */}
              <div className="relative group">
                <FaMapMarkerAlt className="absolute left-4 top-4 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <textarea
                  name="address"
                  value={userData.address}
                  disabled={userData.allow_edit === 0}
                  placeholder="Street Address, City, State"
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 resize-none text-sm"
                />
              </div>
            </div>
          </div>

          {/* Section: Bank Details */}
          <div className="space-y-4 pt-4 border-t border-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-secondary text-[11px] font-black uppercase tracking-widest flex items-center gap-2">
                <span className="w-1 h-3 bg-secondary rounded-full"></span>
                Bank Information
              </h2>
              {userData.allow_edit === 0 && (
                <span className="flex items-center gap-1 text-[10px] text-red-500 font-bold bg-red-500/10 px-2 py-1 rounded-lg">
                  <FaLock size={8} /> LOCKED
                </span>
              )}
            </div>

            <div className="space-y-4">
              {/* Account Number */}
              <div className="relative group">
                <FaCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <input
                  type="text"
                  name="bank_account_number"
                  value={userData.bank_account_number}
                  disabled={userData.allow_edit === 0}
                  placeholder="Bank Account Number"
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                />
              </div>

              {/* IFSC */}
              <div className="relative group">
                <FaCode className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <input
                  type="text"
                  name="ifsc_code"
                  value={userData.ifsc_code}
                  disabled={userData.allow_edit === 0}
                  placeholder="IFSC Code"
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                />
              </div>

              {/* Bank Name */}
              <div className="relative group">
                <FaUniversity className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <input
                  type="text"
                  name="bank_name"
                  value={userData.bank_name}
                  disabled={userData.allow_edit === 0}
                  placeholder="Bank Name"
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                />
              </div>

              {/* UPI ID */}
              <div className="relative group">
                <FaQrcode className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
                <input
                  type="text"
                  name="upi_id"
                  value={userData.upi_id}
                  disabled={userData.allow_edit === 0}
                  placeholder="UPI ID (e.g. name@upi)"
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all disabled:opacity-50 text-sm"
                />
              </div>
            </div>

            {userData.allow_edit === 0 && (
              <p className="text-[10px] text-gray-500 text-center italic mt-2">
                Note: Bank details are locked. Please contact support to enable editing.
              </p>
            )}
          </div>

          {/* Feedback Messages */}
          {error && (
            <div className="flex items-center gap-2 text-red-500 bg-red-500/10 p-4 rounded-2xl border border-red-500/20">
              <FaExclamationTriangle className="flex-shrink-0" />
              <p className="text-xs font-bold uppercase">{error}</p>
            </div>
          )}
          {successMessage && (
            <div className="flex items-center gap-2 text-green-500 bg-green-500/10 p-4 rounded-2xl border border-green-500/20">
              <FaCheckCircle className="flex-shrink-0" />
              <p className="text-xs font-bold uppercase">{successMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          {userData.allow_edit !== 0 && (
            <button
              onClick={onUpdatePress}
              className="w-full bg-secondary text-primary font-black py-5 rounded-2xl shadow-xl transform transition-all active:scale-95 hover:shadow-secondary/20 hover:brightness-110 flex items-center justify-center gap-2 text-lg italic uppercase tracking-tighter"
            >
              Update Profile Details
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;
